'use client';

import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import useGoogleCaptchaReloadScript from './useGoogleCaptchaReloadScript';
import { GoogleCaptchaErrorType } from 'constants/errors';
import { GOOGLE_CAPTCHA_PARAM_KEY_V2, GOOGLE_CAPTCHA_PARAM_KEY_V3 } from 'constants/captcha/google';
import { IdType } from 'types/common';
import { ErrorResponseType } from 'types/request';

const DEFAULT_NAME = 'default';

const CAPTCHA_FAIL_ERRORS = Object.values(GoogleCaptchaErrorType);

interface IGoogleCaptchaOptions {
  disabled?: boolean;
}

export interface IGoogleCaptchaResponse {
  key: IdType;
  isCaptchaError: boolean;
  isCaptchaReady: boolean;
  captchaError: string | null;
  getCaptchaParams: (captchaTokenV2: string) => {
    [GOOGLE_CAPTCHA_PARAM_KEY_V2]?: string;
    [GOOGLE_CAPTCHA_PARAM_KEY_V3]?: string;
  };
  checkCaptchaError: (error: ErrorResponseType) => string;
  resetCaptchaError: () => void;
  resetCaptchaState: () => void;
}

const useGoogleCaptcha = (name: string = DEFAULT_NAME, options: IGoogleCaptchaOptions) => {
  const captchaLogName = `Captcha ${DEFAULT_NAME}`;

  const { disabled } = options || {};

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [key, setKey] = useState(Date.now());
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [captchaTokenV3, setCaptchaTokenV3] = useState(null);
  const [isCaptchaError, setIsCaptchaError] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);

  const verifyCaptchaV3 = useCallback(async () => {
    console.log(captchaLogName, 'Init executeRecaptcha', { extra: { executeRecaptcha } });

    if (executeRecaptcha) {
      const token = await executeRecaptcha(name);

      console.log(captchaLogName, `Generated token ${token}`);

      if (!token) {
        console.error(captchaLogName, 'Token NOT generated', { extra: { token } });
      }

      setCaptchaTokenV3(token);
      setIsCaptchaReady(true);
    } else {
      console.error(captchaLogName, 'executeRecaptcha is EMPTY', { extra: { executeRecaptcha } });
    }
  }, [executeRecaptcha, captchaLogName, name]);

  const checkCaptchaError = error => {
    const errorKey = error.body?.key;
    const isCaptchaFailed = CAPTCHA_FAIL_ERRORS.includes(errorKey);

    if (isCaptchaFailed) {
      setIsCaptchaError(true);
      setCaptchaError(errorKey);
      return errorKey;
    }
  };

  const getCaptchaParams = captchaTokenV2 => {
    return isCaptchaError
      ? { [GOOGLE_CAPTCHA_PARAM_KEY_V2]: captchaTokenV2 }
      : { [GOOGLE_CAPTCHA_PARAM_KEY_V3]: captchaTokenV3 };
  };

  const resetCaptchaError = () => {
    setIsCaptchaError(false);
    setCaptchaError(null);
  };

  const resetCaptchaState = () => {
    setKey(Date.now());
  };

  useGoogleCaptchaReloadScript(isCaptchaReady, { disabled });

  useEffect(() => {
    if (!disabled) {
      verifyCaptchaV3();
    }
  }, [disabled, verifyCaptchaV3]);

  return {
    key,
    isCaptchaError,
    isCaptchaReady,
    captchaError,
    getCaptchaParams,
    checkCaptchaError,
    resetCaptchaError,
    resetCaptchaState,
  };
};

export default useGoogleCaptcha;
